import { useCallback } from 'react';

import getEnabledItems from '@lyearn/core/utils/array/getEnabledItems';
import { TFunction, useTranslation } from '@lyearn/i18n';
import {
  ArchiveOutline,
  CopyOutline,
  DiagonalArrowRightUpFill,
  EditOutline,
  ExternalLinkOutline,
  EyeOutline,
  FolderOutline,
  Link2Fill,
  ListOutline,
  NucleoListTree,
  Trash2Outline,
} from '@lyearn/icons';
import { ActionMenuItemType } from '@lyearn/molecules';
import { FeatureFlags, useFeatureFlags } from '@/helper/FeatureFlags';
import useCopyToClipboard from '@/hooks/useCopyToClipboard';
import useNavigation from '@/hooks/useNavigation';
import useUpdateEntities from '@/modules/tasks/components/AddTasksModal/hooks/useUpdateEntities';
import { ActionItemFragment } from '@/modules/tasks/features/actionItems/graphql/fragments/generated/ActionItem';
import routes from '@/pages/corporate/routes';
import { RelatedEntityTypeEnum } from '@/types/schema';

import getDuplicateTaskValues from '../../utils/getDuplicateTaskValues';
import {
  DashboardActions,
  DashboardActionsDispatch,
  UseDashboardActionsReturnType,
} from '../useDashboardActions';
import useTaskDetailsState from '../useTaskDetailsState';

interface TaskListActionMenuProps {
  setInViewTask: ReturnType<typeof useTaskDetailsState>['setInViewTask'];
  onDeleteActionItem: (id: string) => Promise<string | boolean | null | undefined>;
  setLastActedEntityId?: UseDashboardActionsReturnType['setLastActedEntityId'];
  dispatch: DashboardActionsDispatch;
  entity?: RelatedEntityTypeEnum;
  entityId?: string;
}

export enum TaskActions {
  Edit = 'EDIT',
  View = 'VIEW',
  CopyLink = 'COPY_LINK',
  Delete = 'DELETE',
  Duplicate = 'DUPLICATE',
  GoToOneOnOne = 'GO_TO_ONE_ON_ONE',
  RemoveTask = 'REMOVE_TASK',
  LinkToOKRs = 'LINK_TO_OKRS',
  ConvertToSubtask = 'CONVERT_TO_SUBTASK',
  MoveTo = 'MOVE_TO',
  MoveToList = 'MOVE_TO_LIST',
  MoveToProject = 'MOVE_TO_PROJECT',
}

const getRemoveTranslation = (t: TFunction, entity?: RelatedEntityTypeEnum) => {
  if (entity === RelatedEntityTypeEnum.Okr || entity === RelatedEntityTypeEnum.KeyResult) {
    return t('Task:RemoveFromOKR');
  }

  if (
    entity === RelatedEntityTypeEnum.AgendaItem ||
    entity === RelatedEntityTypeEnum.MeetingInstance
  ) {
    return t('Task:RemoveFromOneOnOne');
  }

  if (entity === RelatedEntityTypeEnum.TeamMeeting) {
    return t('Task:RemoveFromMeeting');
  }

  return '';
};

export default function useTaskListActionMenu(props: TaskListActionMenuProps) {
  const { t } = useTranslation('Task');

  const { setInViewTask, dispatch, entity, entityId, setLastActedEntityId } = props;
  const { onCopyRoute } = useCopyToClipboard();
  const navigation = useNavigation();
  const { onRemoveEntities } = useUpdateEntities({
    entityId,
    entityType: entity,
  });

  const {
    [FeatureFlags.EnableProjectManagementPreReleaseFeatures]: isProjectManagementPreReleaseEnabled,
  } = useFeatureFlags();

  const onMenuItemClick = useCallback(
    (task: ActionItemFragment) => async (val: string) => {
      switch (val as TaskActions) {
        case TaskActions.View:
          setInViewTask(task.id, undefined, entityId);
          break;
        case TaskActions.Edit:
          dispatch({
            type: DashboardActions.EDIT_TASK,
            payload: {
              task,
            },
          });
          setLastActedEntityId?.(entityId);
          break;
        case TaskActions.CopyLink:
          {
            const copyLink = routes.TasksIssue(task.id, task);
            onCopyRoute(`${copyLink}`);
          }
          break;
        case TaskActions.Delete:
          dispatch({
            type: DashboardActions.DELETE_TASK,
            payload: {
              task,
            },
          });
          setLastActedEntityId?.(entityId);
          break;
        case TaskActions.Duplicate:
          dispatch({
            type: DashboardActions.ADD_NEW_TASK,
            payload: {
              task: getDuplicateTaskValues({
                t,
                task,
                entityDetails: {
                  entityType: entity,
                  entityId,
                },
              }),
            },
          });
          setLastActedEntityId?.(entityId);
          break;
        case TaskActions.ConvertToSubtask:
          dispatch({
            type: DashboardActions.CONVERT_TASK_TO_SUBTASK,
            payload: {
              task,
            },
          });
          setLastActedEntityId?.(entityId);
          break;
        case TaskActions.GoToOneOnOne:
          entityId &&
            task.entityId &&
            navigation.push(routes.oneOnOneMeetingDetail(entityId, task.entityId), {
              appendQuery: false,
            });
          break;
        case TaskActions.RemoveTask:
          await onRemoveEntities([task.id]);
          break;
        case TaskActions.MoveToList:
          dispatch({
            type: DashboardActions.MOVE_TASK_TO_LIST,
            payload: {
              task,
            },
          });
          setLastActedEntityId?.(entityId);
          break;
        case TaskActions.MoveToProject:
          dispatch({
            type: DashboardActions.MOVE_TASK_TO_PROJECT,
            payload: {
              task,
            },
          });
          setLastActedEntityId?.(entityId);
          break;
        default:
      }
    },
    [
      setInViewTask,
      entityId,
      dispatch,
      setLastActedEntityId,
      t,
      entity,
      navigation,
      onRemoveEntities,
      onCopyRoute,
    ],
  );

  const isMoveToEnabled = useCallback(
    (task: ActionItemFragment) => {
      if (task?.parentId) return false;
      if (
        entity &&
        [
          RelatedEntityTypeEnum.Okr,
          RelatedEntityTypeEnum.KeyResult,
          RelatedEntityTypeEnum.MeetingInstance,
          RelatedEntityTypeEnum.AgendaItem,
          RelatedEntityTypeEnum.TeamMeeting,
        ].includes(entity)
      ) {
        return false;
      }
      return true;
    },
    [entity],
  );

  const getActionMenu = useCallback(
    (task: ActionItemFragment, isDeleteActionDisabled?: boolean) => ({
      items: getEnabledItems<ActionMenuItemType>([
        {
          label: t('Common:Actions.View'),
          EndIcon: EyeOutline,
          id: TaskActions.View,
          enabled: true,
        },
        {
          label: t('Task:GoToOneOnOne'),
          EndIcon: DiagonalArrowRightUpFill,
          id: TaskActions.GoToOneOnOne,
          enabled: entity === RelatedEntityTypeEnum.Meeting,
        },
        {
          label: t('Common:Actions.Edit'),
          EndIcon: EditOutline,
          id: TaskActions.Edit,
          enabled: true,
        },
        {
          label: t('Common:Actions.Duplicate'),
          EndIcon: CopyOutline,
          id: TaskActions.Duplicate,
          enabled: !task.isAutoSynced && entity !== RelatedEntityTypeEnum.Meeting,
        },
        // {
        //   label: t('Task:LinkToOKRs'),
        //   EndIcon: FlagOutline,
        //   id: TaskActions.LinkToOKRs,
        //   enabled: !task.parentId,
        // },
        {
          label: t('Task:ConvertToSubtask'),
          EndIcon: NucleoListTree,
          id: TaskActions.ConvertToSubtask,
          enabled: isProjectManagementPreReleaseEnabled,
        },
        {
          label: t('Task:MoveTo'),
          EndIcon: ExternalLinkOutline,
          id: TaskActions.MoveTo,
          enabled: isMoveToEnabled(task) ?? false,
          items: [
            {
              label: t('Task:MoveToList'),
              EndIcon: ListOutline,
              id: TaskActions.MoveToList,
              enabled: true,
            },
            {
              label: t('Task:MoveToProject'),
              EndIcon: FolderOutline,
              id: TaskActions.MoveToProject,
              enabled: true,
            },
          ],
        },
        {
          label: t('Common:CopyLink'),
          EndIcon: Link2Fill,
          id: TaskActions.CopyLink,
          enabled: true,
        },
        {
          label: getRemoveTranslation(t, entity),
          EndIcon: ArchiveOutline,
          id: TaskActions.RemoveTask,
          enabled: entity
            ? [
                RelatedEntityTypeEnum.Okr,
                RelatedEntityTypeEnum.KeyResult,
                RelatedEntityTypeEnum.MeetingInstance,
                RelatedEntityTypeEnum.AgendaItem,
                RelatedEntityTypeEnum.TeamMeeting,
              ].includes(entity)
            : false,
        },
        {
          label: t('Common:Actions.Delete'),
          appearance: 'danger',
          EndIcon: Trash2Outline,
          id: TaskActions.Delete,
          enabled: !isDeleteActionDisabled,
        },
      ]) as ActionMenuItemType[],
      onAction: onMenuItemClick,
    }),
    [entity, isMoveToEnabled, isProjectManagementPreReleaseEnabled, onMenuItemClick, t],
  );

  return {
    getActionMenu,
  };
}
